/**
 * Guest-link token identity — the SINGLE source of truth for the three guest
 * links so they can NEVER be confused (host directive 2026-07-17).
 *
 * Used identically by the guest profile link selector AND the Communications
 * Hub, so the colour that means "Confirmation" in one place means exactly the
 * same thing in the other. Invitation and Confirmation are party-level;
 * My Celebration is an individual thank-you link, so its greeting can use
 * the recipient's own Display Name.
 *
 * IMPORTANT: these tokens are different fields on the party and must never be
 * swapped — invitation = party.rsvp_token (route /rsvp|/experience/:token),
 * confirmation = party.celebration_token (route /celebration/:token).
 */

export const LINK_KINDS = {
  invitation: {
    key: 'invitation',
    title: 'Invitation Link',
    short: 'Invitation',
    // slate blue — matches the blue envelope / "come to the event" RSVP flow
    color: '#3b6ea5',
    colorSoft: 'rgba(59, 110, 165, 0.14)',
    colorBorder: 'rgba(59, 110, 165, 0.55)',
    tokenField: 'party_rsvp_token',
    // Guests RSVP via /experience/:token; /rsvp/:token redirects there.
    path: (token) => `/experience/${token}`,
    hint: 'Save-the-date & RSVP. Sent before the event.',
  },
  confirmation: {
    key: 'confirmation',
    title: 'Confirmation Link',
    short: 'Confirmation',
    // champagne gold — matches the celebration site / "you're confirmed"
    color: '#a67c3b',
    colorSoft: 'rgba(198, 167, 90, 0.16)',
    colorBorder: 'rgba(166, 124, 59, 0.6)',
    tokenField: 'party_celebration_token',
    path: (token) => `/celebration/${token}`,
    hint: 'Personalized celebration site. Sent after RSVP.',
  },
  my_celebration: {
    key: 'my_celebration',
    title: 'My Celebration Link',
    short: 'My Celebration',
    color: '#9b4d6b',
    colorSoft: 'rgba(155, 77, 107, 0.13)',
    colorBorder: 'rgba(155, 77, 107, 0.58)',
    tokenField: 'my_celebration_token',
    path: (token) => `/${token}`,
    // This hostname is deliberately separate from the primary event app.
    absoluteOrigin: process.env.REACT_APP_MY_CELEBRATION_ORIGIN || 'https://mycelebration.mvitations.com',
    hint: 'Private thank-you note and shared album. Sent after the event.',
  },
};

export const LINK_ORDER = ['invitation', 'confirmation', 'my_celebration'];

/** Build the absolute guest-facing URL for a kind, or '' when the token is missing. */
export const buildGuestLink = (kind, guestOrParty, origin) => {
  const meta = LINK_KINDS[kind];
  if (!meta) return '';
  const token = (guestOrParty && guestOrParty[meta.tokenField]) || '';
  if (!token) return '';
  const base = (meta.absoluteOrigin || origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return `${base}${meta.path(token)}`;
};
